/**
 * Small readers for a captured chart page, shared by the EMR parsers
 * (telusChr.ts says where on its EMR's page each thing is).
 *
 * Everything here works on the captured copy, parsed with DOMParser, which
 * never runs scripts or fetches anything.
 */

/** Whitespace collapsed and trimmed. */
export function clean(text: string | null | undefined): string {
  return (text ?? "").replace(/[\s ]+/g, " ").trim();
}

/** The comparable form of a label: lower case, no trailing colon or required-field mark. */
export function labelKey(text: string): string {
  return clean(text)
    .toLowerCase()
    .replace(/[\s:*]+$/u, "")
    .replace(/^[\s*]+/u, "");
}

export function parseCapturedPage(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

/** The text of an element's own text nodes, without its children's. */
export function ownText(element: Element | null | undefined): string {
  if (!element) return "";
  return clean(
    Array.from(element.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join(" "),
  );
}

const CONTROLS = "input, select, textarea";

/** What a form control on the captured page holds (the capture keeps typed values as attributes). */
export function controlValue(control: Element | null | undefined): string {
  if (!control) return "";
  if (control instanceof HTMLSelectElement) {
    const selected = control.querySelector("option[selected]") ?? control.options[0];
    return clean(selected?.textContent);
  }
  if (control instanceof HTMLTextAreaElement) return clean(control.textContent);
  if (control instanceof HTMLInputElement) {
    const type = (control.getAttribute("type") ?? "text").toLowerCase();
    if (type === "checkbox" || type === "radio")
      return control.hasAttribute("checked") ? "Yes" : "";
    return clean(control.getAttribute("value"));
  }
  return "";
}

const BLOCK_TAGS = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "BR",
  "DD",
  "DIV",
  "DL",
  "DT",
  "FIELDSET",
  "FIGURE",
  "FOOTER",
  "FORM",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "HR",
  "LI",
  "MAIN",
  "NAV",
  "OL",
  "P",
  "PRE",
  "SECTION",
  "TABLE",
  "TR",
  "UL",
]);

/**
 * An element's text as the page lays it out, a line per block and line break,
 * list items marked "- ". What the page hid (`data-bobdoc-hidden`, such as an
 * unfilled template field in a note) is left out: this is the text a reader saw.
 */
export function blockText(element: Element): string {
  const parts: string[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent ?? "");
      return;
    }
    if (!(node instanceof Element) || node.hasAttribute("data-bobdoc-hidden")) return;
    if (node.matches(CONTROLS)) {
      parts.push(` ${controlValue(node)} `);
      return;
    }
    const block = BLOCK_TAGS.has(node.tagName);
    if (block) parts.push("\n");
    if (node.tagName === "LI") parts.push("- ");
    node.childNodes.forEach(walk);
    if (block) parts.push("\n");
  };
  element.childNodes.forEach(walk);
  return parts
    .join("")
    .split("\n")
    .map((line) => clean(line))
    .filter((line) => line !== "" && line !== "-")
    .join("\n");
}

export interface LabelledControl {
  /** The label as the page wrote it, without a trailing colon. */
  readonly label: string;
  readonly value: string;
}

/** The one control in a label's field: the closest ancestor holding exactly one. */
function nearestControl(label: Element): Element | undefined {
  let node = label.parentElement;
  for (let depth = 0; node && depth < 3; depth += 1, node = node.parentElement) {
    const inside = node.querySelectorAll(CONTROLS);
    if (inside.length === 1) return inside[0];
    if (inside.length > 1) return undefined;
  }
  return undefined;
}

/**
 * A form's fields by label, in page order: the control a label names (`for`),
 * holds, or sits beside. Keyed by `labelKey`; the first label of a name wins.
 */
export function labelledControls(root: Element): Map<string, LabelledControl> {
  const found = new Map<string, LabelledControl>();
  const controls = Array.from(root.querySelectorAll(CONTROLS));
  for (const label of Array.from(root.querySelectorAll("label"))) {
    const text = clean(
      Array.from(label.childNodes)
        .filter((node) => !(node instanceof Element && node.matches(CONTROLS)))
        .map((node) => node.textContent)
        .join(" "),
    );
    const key = labelKey(text);
    if (key === "" || found.has(key)) continue;
    const target = label.getAttribute("for");
    const control =
      (target ? controls.find((candidate) => candidate.id === target) : undefined) ??
      label.querySelector(CONTROLS) ??
      nearestControl(label);
    if (control)
      found.set(key, { label: text.replace(/[\s:*]+$/u, ""), value: controlValue(control) });
  }
  return found;
}

const MONTHS: Readonly<Record<string, number>> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  sept: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

function isoDate(year: number, month: number, day: number): string | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A full date the page wrote unambiguously, as `YYYY-MM-DD`: year first
 * (2024-06-02, 2024/06/02) or with the month named (Jun 2, 2024; CHR's
 * "Jun, 02 2024"; 2 June 2024; 02-Jun-2024). Day/month numerals in either
 * order are ambiguous and refused.
 */
export function readDate(text: string): string | null {
  const value = clean(text);
  const monthOf = (name: string) =>
    MONTHS[name.slice(0, 4).toLowerCase()] ?? MONTHS[name.slice(0, 3).toLowerCase()];
  let match = /\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/.exec(value);
  if (match) return isoDate(Number(match[1]), Number(match[2]), Number(match[3]));
  match = /\b([A-Za-z]{3,9})\.?,?\s+(\d{1,2}),?\s+(\d{4})\b/.exec(value);
  if (match) {
    const month = monthOf(match[1] ?? "");
    return month === undefined ? null : isoDate(Number(match[3]), month, Number(match[2]));
  }
  match = /\b(\d{1,2})[\s-]([A-Za-z]{3,9})\.?[\s-](\d{4})\b/.exec(value);
  if (match) {
    const month = monthOf(match[2] ?? "");
    return month === undefined ? null : isoDate(Number(match[3]), month, Number(match[1]));
  }
  return null;
}
